import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, PlusCircle, Shuffle, Search as SearchIcon, X, Dices, MapPin, Share2, LogIn, Sparkles } from 'lucide-react';
import PrayerCard from '../components/PrayerCard';
import CategoryChips from '../components/CategoryChips';
import { listPrayers, fetchSharedPrayers, removeSharedOpen, hideFromRandom, fetchLiveCounts, dismissPrayer, fetchSentRandom, randomFindPrayer, getRandomDiscoveries, addRandomDiscovery, removeRandomDiscovery, selectRandomDiscovery } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { searchFocusRef } from '../lib/searchFocus';
import { sortLiveFirst, openedTime } from '../lib/feedSort';
import Notifications from './Notifications';

/** Map a category chip to the backend `sort` query. `Search` runs a
 * text query on submit and returns nothing when no query is present —
 * it no longer surfaces a personalised watch-history feed. */
const CHIP_TO_SORT = {
  Search: 'latest',
  Nearby: 'nearby',
  'From Random': 'random',
};

// Cap on how many recent search terms we remember per user.
const SEARCH_HISTORY_LIMIT = 10;

// Shared hero-card shell used for every empty-state tab on the Home
// page so Shared / Nearby / Random / Search stay visually consistent.
// Hoisted out of the Home component to satisfy React's stable-
// identity contract (React would otherwise unmount + remount the
// subtree on every render, losing internal state).
const HeroEmpty = ({ icon, title, subtitle, cta, testid }) => (
  <div
    className="mt-6 mx-auto max-w-lg rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/60 shadow-sm px-8 py-10 flex flex-col items-center text-center"
    data-testid={testid}
  >
    <div
      aria-hidden="true"
      className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 dark:text-amber-300 mb-4"
    >
      {icon}
    </div>
    <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
      {title}
    </h2>
    {subtitle && (
      <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed max-w-sm">
        {subtitle}
      </p>
    )}
    {cta && <div className="mt-5">{cta}</div>}
  </div>
);

const Home = () => {
  const { user, loading: authLoading } = useAuth();
  const { count: notificationCount } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = (searchParams.get('q') || '').trim();
  // Default landing tab is **Shared** for every visitor — signed-in or
  // anonymous. Anonymous users see a "Sign in to view prayers shared
  // with you" prompt on that tab, which doubles as an onboarding nudge.
  // We seed `activeChip` with the literal 'Shared' so the first paint
  // is correct even before AuthContext resolves — no flicker, no race
  // where a hardcoded index-0 tab briefly wins. The only exception is
  // a `?q=` deep-link, which opens on the Search tab instead so the
  // results are visible immediately.
  const initialQFromUrl = (searchParams.get('q') || '').trim();
  const [activeChip, setActiveChip] = useState(initialQFromUrl ? 'Search' : 'Shared');
  const [prayers, setPrayers] = useState([]);
  // Fresh live-praying counts per prayer id, refreshed every 5s. Merged
  // into each PrayerCard's prop so the LIVE badge stays in sync without
  // any prop plumbing beyond the existing prayer object.
  const [liveCountsById, setLiveCountsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bumped every time the user (re-)clicks "Random Pick" so the request
  // triggers a fresh $sample and returns a different prayer.
  const [randomNonce, setRandomNonce] = useState(0);

  // ------------------------------------------------------------------
  // Per-USER persisted flags (localStorage keys are namespaced by user
  // id — otherwise a brand-new sign-in on a browser previously used by
  // someone else would inherit that person's dismissed / visit state.)
  //
  // IMPORTANT: search history is ONLY hydrated / persisted when a user
  // is signed in. Anonymous sessions must always show an empty list,
  // even if some other user typed queries in this browser earlier —
  // otherwise those pills leak to whoever loads the page next.
  // ------------------------------------------------------------------
  const SEARCH_HISTORY_KEY = user?.user_id
    ? `pwmn_search_history:${user.user_id}`
    : null;

  const [searchHistory, setSearchHistory] = useState([]);

  // ------------------------------------------------------------------
  // Randomly-found-recently ledger (Random tab).
  // Each pill represents ONE prayer previously surfaced by clicking
  // "Randomly Find". We store the full prayer object (capped at 10) so
  // clicking a pill can re-render the card without a network round-trip.
  // Persisted per-user; anonymous sessions keep the list in memory only.
  // ------------------------------------------------------------------
  // ------------------------------------------------------------------
  // Random-tab session results.
  // Every "Randomly Find" click appends a fresh prayer (dedupe by id).
  // The list resets on page reload and user switch — there's no
  // persistent history anymore, just an in-session accumulation.
  // ------------------------------------------------------------------
  const [randomSessionResults, setRandomSessionResults] = useState([]);
  const [randomCurrentId, setRandomCurrentId] = useState(null);
  const [randomHydrating, setRandomHydrating] = useState(true);
  const [randomFinding, setRandomFinding] = useState(false);
  const [randomError, setRandomError] = useState(null);

  useEffect(() => {
    // Restore Random-tab state from the backend for signed-in users.
    // Anonymous visitors get an empty session (no server state to
    // hydrate), matching the previous behaviour. Runs on every mount
    // so Home unmount/remount cycles (navigating to another page and
    // back) always end with the pills + selected card intact.
    if (!user?.user_id) {
      setRandomSessionResults([]);
      setRandomCurrentId(null);
      setRandomHydrating(false);
      return;
    }
    let cancelled = false;
    setRandomHydrating(true);
    (async () => {
      try {
        const { discoveries, selected_id } = await getRandomDiscoveries();
        if (cancelled) return;
        setRandomSessionResults(Array.isArray(discoveries) ? discoveries : []);
        setRandomCurrentId(selected_id || (discoveries?.[0]?.id ?? null));
      } catch (e) {
        console.warn('[Random] hydrate failed', e);
        if (!cancelled) {
          setRandomSessionResults([]);
          setRandomCurrentId(null);
        }
      } finally {
        if (!cancelled) setRandomHydrating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id]);

  // Append a prayer to the session results (dedupe by id) and make it
  // the currently-displayed card. New entries land at the TOP of the
  // pill row so the newest find is always the leftmost pill. Also
  // persists to the backend so the state survives a reload / logout.
  const appendToSessionResults = (p) => {
    setRandomSessionResults((prev) => {
      const filtered = prev.filter((x) => x.id !== p.id);
      return [p, ...filtered];
    });
    setRandomCurrentId(p.id);
    if (user?.user_id) {
      addRandomDiscovery(p.id).catch((e) =>
        console.warn('[Random] persist add failed', e)
      );
    }
  };

  const handleRandomlyFind = async () => {
    if (randomFinding) return;
    setRandomFinding(true);
    setRandomError(null);
    try {
      const p = await randomFindPrayer();
      appendToSessionResults(p);
    } catch (e) {
      console.error('random find failed', e);
      setRandomError(
        e?.response?.status === 404
          ? "No public prayers from the last 30 days right now. Try again later."
          : 'Could not fetch a random prayer. Please try again.'
      );
    } finally {
      setRandomFinding(false);
    }
  };

  const removeSessionResult = (id) => {
    setRandomSessionResults((prev) => {
      const next = prev.filter((p) => p.id !== id);
      setRandomCurrentId((curr) => {
        if (curr !== id) return curr;
        const fallback = next[0]?.id ?? null;
        // The removed card was the selected one — if there's a
        // fallback, persist it so the next reload keeps the pill
        // highlight aligned with the visible card.
        if (fallback && user?.user_id) {
          selectRandomDiscovery(fallback).catch((e) =>
            console.warn('[Random] persist fallback select failed', e)
          );
        }
        return fallback;
      });
      return next;
    });
    if (user?.user_id) {
      removeRandomDiscovery(id).catch((e) =>
        console.warn('[Random] persist remove failed', e)
      );
    }
  };

  // Set which pill/card is the currently-active one. Updates local
  // state immediately (optimistic) and persists to the backend so the
  // highlight and single-card view survive a full page reload.
  const selectRandomDiscoveryLocal = (id) => {
    setRandomCurrentId(id);
    if (user?.user_id) {
      selectRandomDiscovery(id).catch((e) =>
        console.warn('[Random] persist select failed', e)
      );
    }
  };

  // Re-hydrate the per-user search history whenever the signed-in user
  // changes (including anonymous → authenticated transitions). Falls
  // through to an empty list for logged-out visitors so they never see
  // a previous user's queries.
  useEffect(() => {
    // Proactively purge the legacy anonymous bucket. Prior versions
    // wrote every unauthenticated search under `pwmn_search_history:anon`
    // — those pills would then leak into every future logged-out view on
    // the same browser. Clear it once here so old sessions self-heal.
    try { localStorage.removeItem('pwmn_search_history:anon'); } catch { /* noop */ }
    if (!SEARCH_HISTORY_KEY) {
      setSearchHistory([]);
      return;
    }
    try {
      const raw = localStorage.getItem(SEARCH_HISTORY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      setSearchHistory(Array.isArray(parsed) ? parsed.filter(t => typeof t === 'string' && t.trim()) : []);
    } catch {
      // Storage unavailable — behave as no search history.
      setSearchHistory([]);
    }
  }, [SEARCH_HISTORY_KEY]);

  const persistSearchHistory = (next) => {
    setSearchHistory(next);
    if (!SEARCH_HISTORY_KEY) return; // don't leak anonymous searches into a shared bucket
    try { localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
  };

  // Auto-populate the Search tab with the most-recent history pill the
  // FIRST time it's opened WITHOUT a query this session. If the user
  // has any recent searches saved, land them on that query's results
  // instead of the blank "Perform a search" empty state. Guarded by
  // `autoRecentAppliedRef` so once we've applied the most-recent (or
  // the user starts a fresh search), we don't yank them back into it
  // on every subsequent Search-tab click.
  const autoRecentAppliedRef = useRef(false);
  useEffect(() => {
    if (autoRecentAppliedRef.current) return;
    if (activeChip !== 'Search') return;
    // A query is already active — either a deep link or a user-typed
    // search. Don't overwrite it, but leave the ref FALSE so a later
    // clear + re-open of the Search tab can still auto-fill from
    // history.
    if (searchQuery) return;
    if (!Array.isArray(searchHistory) || searchHistory.length === 0) return;
    const mostRecent = (searchHistory[0] || '').trim();
    if (!mostRecent) return;
    autoRecentAppliedRef.current = true;
    setSearchParams({ q: mostRecent }, { replace: true });
  }, [activeChip, searchQuery, searchHistory, setSearchParams]);

  // Auto-jump to the Search tab exactly ONCE per new header-search.
  // After that the user is free to click Nearby / Trending / Latest /
  // Random Pick without being snapped back — the query stays in the
  // URL so returning to Search later restores the results.
  const lastAutoJumpRef = useRef('');
  useEffect(() => {
    if (searchQuery && lastAutoJumpRef.current !== searchQuery) {
      lastAutoJumpRef.current = searchQuery;
      setActiveChip('Search');
    }
    if (!searchQuery) {
      lastAutoJumpRef.current = '';
    }
  }, [searchQuery]);

  // Whenever a new header-search resolves, prepend the term into the
  // per-user history pill list (dedupe + cap). Skipped entirely for
  // anonymous visitors — their queries stay in-memory only and are
  // never written to any localStorage bucket.
  useEffect(() => {
    if (!searchQuery) return;
    setSearchHistory((prev) => {
      const filtered = prev.filter(t => t.toLowerCase() !== searchQuery.toLowerCase());
      const next = [searchQuery, ...filtered].slice(0, SEARCH_HISTORY_LIMIT);
      if (SEARCH_HISTORY_KEY) {
        try { localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
      }
      return next;
    });
  }, [searchQuery, SEARCH_HISTORY_KEY]);

  useEffect(() => {
    let cancelled = false;
    // ---- Notifications tab flow ----
    // No prayer feed to load; the tab renders `<Notifications embedded />`
    // which pulls its own data. Clear the grid so switching to this
    // chip doesn't leave stale prayer cards behind.
    if (activeChip === 'Notifications') {
      setPrayers([]);
      setLoading(false);
      setError(null);
      return () => { cancelled = true; };
    }
    (async () => {
      // ---- Random tab flow ----
      // The Random tab renders a one-off "Randomly Find" flow — no
      // background listing at all. We just clear the shared grid so
      // switching to this chip doesn't leave stale cards behind.
      if (activeChip === 'Random') {
        setPrayers([]);
        setLoading(false);
        setError(null);
        return;
      }
      // ---- Search tab flow ----
      // The tab now ONLY serves search results. Without a query it
      // renders the "Perform a search to get started" empty state.
      if (activeChip === 'Search') {
        if (!searchQuery) {
          setPrayers([]);
          setLoading(false);
          setError(null);
          return;
        }
        setLoading(true);
        setError(null);
        try {
          const data = await listPrayers({ limit: 60, sort: 'latest', q: searchQuery });
          if (!cancelled) setPrayers(data);
        } catch (e) {
          if (!cancelled) setError('Failed to load prayers');
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }
      // ---- Shared tab flow ----
      // Backed by /api/prayers/shared — the personal ledger of prayers
      // the current user opened via a share link (newest first). Split
      // into two tabs by sharer origin:
      //   • "Shared"         → member-authored shares (`shared_by` set)
      //   • "From Anonymous" → anonymous copy-paste shares (`shared_by`
      //                        missing/null)
      if (activeChip === 'Shared' || activeChip === 'From Anonymous') {
        setLoading(true);
        setError(null);
        try {
          const scope = activeChip === 'Shared' ? 'member' : 'anonymous';
          // Anonymous callers also fetch — the backend returns the
          // @praynowtogether "Welcome" prayer as a public landing card
          // so first-time visitors see something meaningful instead of
          // a sign-in gate.
          const data = await fetchSharedPrayers({ limit: 60, sharer_scope: scope });
          if (!cancelled) setPrayers(data);
        } catch (e) {
          if (!cancelled) setError('Failed to load shared prayers');
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }
      // ---- To Random tab flow ----
      // Backed by /api/prayers/sent-random — the current user's own
      // prayers that were fan-out'd to a random recipient on creation.
      // Guarded on `user` so anonymous viewers just see an empty list
      // (they can't have authored anything yet).
      if (activeChip === 'To Random') {
        setLoading(true);
        setError(null);
        try {
          const data = user ? await fetchSentRandom() : [];
          if (!cancelled) setPrayers(data);
        } catch (e) {
          if (!cancelled) setError('Failed to load your outgoing invites');
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const sort = CHIP_TO_SORT[activeChip] || 'latest';
        // "Random Pick" always returns exactly one prayer per the spec.
        const limit = sort === 'random' ? 1 : 60;
        const data = await listPrayers({ limit, sort });
        if (!cancelled) setPrayers(data);
      } catch (e) {
        if (!cancelled) setError('Failed to load prayers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeChip, randomNonce, searchQuery, user]);

  // Intra-Home tab-switch state preservation for the Search tab.
  // When the user leaves the Search tab (Search → Nearby), we snap-
  // shot the active query into `lastSearchQueryRef`. When they come
  // back to Search WITHOUT a query in the URL, we restore that
  // snapshot instead of dropping them on the empty "Perform a search"
  // state. Layered on top of the first-open auto-fill from history:
  // if there's no snapshot yet (very first Search visit this session),
  // the first-open effect picks up the most-recent from history.
  const lastSearchQueryRef = useRef('');

  const handleChipChange = (chip) => {
    if (chip === activeChip && chip === 'From Random') {
      // Re-clicking Random reshuffles.
      setRandomNonce((n) => n + 1);
      return;
    }
    // If we're LEAVING Search, snapshot the current query so the
    // return trip can restore it.
    if (activeChip === 'Search' && chip !== 'Search') {
      const q = (searchParams.get('q') || '').trim();
      lastSearchQueryRef.current = q;
    }
    setActiveChip(chip);
    // Activating the Search tab: focus the header search input so
    // the mobile on-screen keyboard reliably pops up (see
    // searchFocus.js for the gesture-chain constraint) and restore
    // any snapshotted query so intra-session tab switching brings
    // the user back to their last active search + results.
    if (chip === 'Search') {
      const urlQ = (searchParams.get('q') || '').trim();
      if (!urlQ && lastSearchQueryRef.current) {
        setSearchParams({ q: lastSearchQueryRef.current }, { replace: true });
      }
      if (typeof searchFocusRef.focusNow === 'function') {
        searchFocusRef.focusNow();
      }
    } else {
      // Any non-Search tab (Shared / Nearby / Random / From Random /
      // From Anonymous / To Random) collapses the mobile expanded
      // search bar so the header returns to its icon-only chrome.
      // No-op on desktop where the bar is always visible. Preserves
      // the query text + URL so returning to Search restores state.
      if (typeof searchFocusRef.collapseNow === 'function') {
        searchFocusRef.collapseNow();
      }
    }
  };

  const runHistoryTerm = (term) => {
    setActiveChip('Search');
    setSearchParams({ q: term }, { replace: false });
  };

  const removeHistoryTerm = (term) => {
    const next = searchHistory.filter(t => t !== term);
    persistSearchHistory(next);
    // If that was the last pill, cascade into the same reset the
    // "Clear all" button performs: wipe the URL `?q=` (which empties
    // the header search input) and drop the results list so the tab
    // returns to its pristine empty state. Without this, dismissing
    // the last pill would leave a stale search + cards on screen with
    // nothing to indicate why they're still there.
    if (next.length === 0) {
      if (searchParams.get('q')) {
        setSearchParams({}, { replace: true });
      }
      setPrayers([]);
      // Same focus-restore as Clear All — dismissing the last pill is
      // functionally identical from the user's perspective.
      if (typeof searchFocusRef.focusNow === 'function') {
        searchFocusRef.focusNow();
      }
    }
  };

  const clearAllHistory = () => {
    // "Clear All" now returns the Search tab to its pristine empty
    // state: (1) drop every recent-search pill, (2) clear the URL
    // `?q=` param so the input box empties and the useEffect that
    // populates `prayers` short-circuits, (3) proactively empty the
    // in-memory results list so any card that hadn't been un-mounted
    // by the search effect disappears immediately. Together these
    // three steps make the tab look identical to a fresh visit.
    persistSearchHistory([]);
    // Wipe the URL query. Also clears the input box because the box's
    // value is bound to `searchQuery = searchParams.get('q')`.
    if (searchParams.get('q')) {
      setSearchParams({}, { replace: true });
    }
    setPrayers([]);
    // Return focus to the search bar so the user can immediately
    // start a fresh search after wiping.
    if (typeof searchFocusRef.focusNow === 'function') {
      searchFocusRef.focusNow();
    }
  };

  // Remove a prayer from the Shared tab. Optimistic UI update — pull the
  // card out of the local list immediately, then hit the backend to delete
  // the ledger entry. On failure, refetch so the list re-syncs with the
  // server rather than staying in an inconsistent state.
  const handleRemoveFromShared = async (prayer) => {
    if (!prayer?.id) return;
    const prevList = prayers;
    setPrayers((list) => list.filter((p) => p.id !== prayer.id));
    try {
      await removeSharedOpen(prayer.id);
    } catch (e) {
      console.error('remove from Shared failed', e);
      setPrayers(prevList);
    }
  };

  // Hide a prayer from the Random tab. Same optimistic pattern as the
  // Shared removal: pull the card out immediately, POST to the backend,
  // roll back on failure. The `random_hidden` ledger is per-user and
  // persistent — the same prayer will not surface for this viewer again.
  const handleRemoveFromRandom = async (prayer) => {
    if (!prayer?.id) return;
    const prevList = prayers;
    setPrayers((list) => list.filter((p) => p.id !== prayer.id));
    try {
      await hideFromRandom(prayer.id);
    } catch (e) {
      console.error('hide from Random failed', e);
      setPrayers(prevList);
    }
  };

  // Dismiss a prayer from the invite feeds (All Invites / Random
  // Invites / Nearby). Same optimistic pattern as Shared/Random removal
  // but persistent across all three tabs — the `dismissed_prayers` ledger
  // is per-user and permanent, so the prayer will never resurface on
  // those tabs for this viewer.
  const handleDismiss = async (prayer) => {
    if (!prayer?.id) return;
    const prevList = prayers;
    setPrayers((list) => list.filter((p) => p.id !== prayer.id));
    try {
      await dismissPrayer(prayer.id);
    } catch (e) {
      console.error('dismiss prayer failed', e);
      setPrayers(prevList);
    }
  };

  // Poll live-praying counts for the currently visible cards every 5s so
  // the LIVE badge tracks the same 15s participant window that the watch
  // page uses. Runs whenever the visible prayer set changes; auto-clears
  // when the list is empty so we don't hammer the endpoint on empty tabs.
  useEffect(() => {
    if (!prayers.length) {
      setLiveCountsById({});
      return undefined;
    }
    let cancelled = false;
    const ids = prayers.map((p) => p.id);
    const tick = async () => {
      try {
        const counts = await fetchLiveCounts(ids);
        if (!cancelled) setLiveCountsById(counts);
      } catch { /* silent — badge just stays on its last known state */ }
    };
    tick();
    const interval = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // Depend on the joined id string so we don't re-schedule on the same set
    // when React re-renders (e.g. after loading spinner toggles).
  }, [prayers.map((p) => p.id).join('|')]);

  const isRandom = activeChip === 'From Random';
  const isRandomTab = activeChip === 'Random';
  const isSearch = activeChip === 'Search';
  const isShared = activeChip === 'Shared' || activeChip === 'From Anonymous';
  const isNearby = activeChip === 'Nearby';
  const isNotifications = activeChip === 'Notifications';
  // Shared tab specifically requests live-first ordering: any prayer
  // with someone currently praying floats to the top, then the rest
  // sort by when THIS viewer opened the shared link (newest first).
  // Random / Search / Nearby / Notifications keep their server-side
  // order untouched per product spec.
  const displayedPrayers = useMemo(() => {
    if (!isShared) return prayers;
    return sortLiveFirst(prayers, liveCountsById, openedTime);
  }, [isShared, prayers, liveCountsById]);
  // The X close-button on cards is only wired up on the three "invite"
  // tabs and only for authenticated users (dismissal is persistent, so
  // it needs a signed-in identity to write against).
  const canDismiss = !!user && (isShared || isRandom || isNearby || isSearch);
  // Show the "Perform a search to get started" card whenever the
  // Search tab has no query submitted (or the query returned no
  // results) — the tab is single-purpose so this is the only
  // empty-state it needs.
  const isSearchUnsubmitted = isSearch && !searchQuery && !loading;
  const isEmpty = !loading && !error && prayers.length === 0 && !isSearchUnsubmitted;

  // On Trending, place the prayer grid alongside a right-side tag rail.
  // On other tabs we render the plain full-width grid as before.
  const gridSection = (
    <>
      {loading && (
        <div className="py-20 text-center text-zinc-500 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Loading prayers…
        </div>
      )}

      {!loading && error && (
        <div className="py-20 text-center text-zinc-500">{error}</div>
      )}

      {isEmpty && (() => {
        if (activeChip === 'Nearby') {
          // Dynamic radius pulled from the viewer's profile so the
          // empty-state copy always reflects their actual setting
          // rather than a hardcoded distance. Falls back to the
          // server-side default (50) for anonymous viewers whose
          // `user` object hasn't been hydrated yet.
          const radius = Number(user?.nearby_radius_mi) || 25;
          return (
            <HeroEmpty
              icon={<MapPin size={26} strokeWidth={2.25} />}
              title={`No public prayers found within ${radius} miles.`}
              cta={
                <Link
                  to="/add-prayer"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 text-sm font-medium"
                >
                  <PlusCircle size={16} /> Share a prayer
                </Link>
              }
              testid="home-empty-state"
            />
          );
        }
        if (isSearch) {
          return (
            <HeroEmpty
              icon={<SearchIcon size={26} strokeWidth={2.25} />}
              title={<>No public prayers matched &ldquo;{searchQuery}&rdquo;. Try a different keyword or handle.</>}
              testid="home-empty-state"
            />
          );
        }
        if (isShared) {
          if (!user) {
            return (
              <HeroEmpty
                icon={<LogIn size={26} strokeWidth={2.25} />}
                title="Sign in or create an account to view prayers shared with you."
                testid="home-empty-state"
              />
            );
          }
          return (
            <HeroEmpty
              icon={<Share2 size={26} strokeWidth={2.25} />}
              title={
                activeChip === 'Shared'
                  ? 'When you open prayer links shared with you by email, text message or media, they will be saved here.'
                  : "You haven't yet opened an anonymous prayer link."
              }
              testid="home-empty-state"
            />
          );
        }
        return (
          <HeroEmpty
            icon={<Sparkles size={26} strokeWidth={2.25} />}
            title="No prayers to show here yet"
            testid="home-empty-state"
          />
        );
      })()}

      {!loading && prayers.length > 0 && (
        <div
          className={
            isRandom
              ? 'max-w-md mx-auto pb-10 pt-2'
              : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-x-4 gap-y-8 pb-10'
          }
          data-testid={`home-grid-${(activeChip || 'loading').toLowerCase().replace(/\s+/g, '-')}`}
        >
          {displayedPrayers.map(p => {
            // Merge in the freshest live-praying count so the LIVE badge
            // reflects the polling result without the parent having to
            // clone the whole list every tick.
            const merged = liveCountsById[p.id] !== undefined
              ? { ...p, online_praying_now_count: liveCountsById[p.id] }
              : p;
            // Tag each random-share row with its role relative to the
            // current viewer so the PrayerCard swaps its generic "Random"
            // badge for a role-specific one ("You sent to a random member"
            // when the viewer is the author, "Received Randomly" when the
            // viewer is the recipient). Applied universally — every tab
            // (Shared / Random / Nearby / Search / any future one) uses
            // the same visual language for random-share interactions.
            // Non-random rows leave this null and keep their default look.
            const sharedContextRole = (merged.is_random_share && user)
              ? (merged.user_id === user.user_id ? 'authored' : 'received')
              : null;
            return (
              <PrayerCard
                key={p.id}
                prayer={merged}
                sharedContextRole={sharedContextRole}
                showGenericRandomPill={isRandom}
                onDismiss={canDismiss ? handleDismiss : undefined}
                onRemove={
                  isShared
                    ? handleRemoveFromShared
                    : isRandom
                      ? handleRemoveFromRandom
                      : undefined
                }
              />
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div className="px-4 sm:px-6">
      <CategoryChips
        active={activeChip}
        onChange={handleChipChange}
        notificationCount={notificationCount}
      />

      {/* Notifications tab — renders the inbox in place of the prayer feed.
          The count badge on the chip is driven by the shared
          `NotificationsContext`, so real-time SSE updates and optimistic
          `pwm:notifications-decrement` events still tick the number
          exactly like the old header bell did. */}
      {isNotifications && (
        <div className="mt-2" data-testid="home-notifications-tab-body">
          <Notifications embedded />
        </div>
      )}

      {isSearch && searchHistory.length > 0 && (
        <div
          className="mb-4 flex items-center gap-2 flex-wrap"
          data-testid="search-history-pills"
        >
          <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide pr-1">
            Recent searches
          </span>
          {searchHistory.map((term) => {
            const isActive = term.toLowerCase() === searchQuery.toLowerCase();
            const slug = term.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'term';
            return (
              <div
                key={term}
                data-testid={`search-pill-${slug}`}
                className={`inline-flex items-center h-8 rounded-full border text-sm transition-colors ${
                  isActive
                    ? 'bg-amber-100 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200'
                    : 'bg-zinc-100 dark:bg-zinc-800 border-transparent text-zinc-800 dark:text-zinc-100 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                }`}
              >
                <button
                  type="button"
                  onClick={() => runHistoryTerm(term)}
                  data-testid={`search-pill-run-${slug}`}
                  className="pl-3 pr-2 h-full inline-flex items-center gap-1.5 font-medium"
                  title={`Search again for "${term}"`}
                >
                  <SearchIcon size={12} className="opacity-70" />
                  <span className="max-w-[160px] truncate">{term}</span>
                </button>
                <button
                  type="button"
                  onClick={() => removeHistoryTerm(term)}
                  data-testid={`search-pill-remove-${slug}`}
                  aria-label={`Remove ${term} from recent searches`}
                  className="mr-1 p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 text-current"
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={clearAllHistory}
            data-testid="search-history-clear-all"
            className="ml-1 text-xs text-zinc-500 hover:text-zinc-800 dark:hover:text-white underline underline-offset-2"
          >
            Clear all
          </button>
        </div>
      )}

      {isSearchUnsubmitted && (
        <div
          className="mt-6 mx-auto max-w-lg rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/60 shadow-sm px-8 py-10 flex flex-col items-center text-center"
          data-testid="search-tab-empty-instruction"
        >
          <div
            aria-hidden="true"
            className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 dark:text-amber-300 mb-4"
          >
            <SearchIcon size={26} strokeWidth={2.25} />
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
            Perform a search
          </h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed max-w-sm">
            Use the search bar at the top to find prayers by keyword, topic, or user handle.
            Your recent searches are saved so you can jump back into them.
          </p>
        </div>
      )}

      {isRandom && !loading && prayers.length > 0 && (
        <div className="mb-3 flex items-center justify-end">
          <button
            type="button"
            onClick={() => setRandomNonce((n) => n + 1)}
            data-testid="random-shuffle-btn"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-white"
          >
            <Shuffle size={16} /> Shuffle again
          </button>
        </div>
      )}

      {isRandomTab && (
        <div data-testid="random-tab-panel">
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={handleRandomlyFind}
              disabled={randomFinding}
              data-testid="randomly-find-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-xs font-semibold shadow-sm"
            >
              {randomFinding
                ? <Loader2 size={12} className="animate-spin" />
                : <Dices size={12} />}
              Select a Random Prayer
            </button>
            <span className="text-xs text-zinc-500 dark:text-zinc-400">
              Surface one public prayer from the last 30 days.
            </span>
          </div>

          {randomError && (
            <div
              className="py-6 text-sm text-red-600 dark:text-red-400"
              data-testid="random-error"
            >
              {randomError}
            </div>
          )}

          {/* ---- Session results (accumulate on each click) ---- */}
          {randomHydrating && (
            <div
              className="py-16 flex items-center justify-center gap-2 text-zinc-500"
              data-testid="random-hydrating"
            >
              <Loader2 className="animate-spin" size={16} /> Restoring your random findings…
            </div>
          )}
          {!randomHydrating && randomSessionResults.length === 0 && !randomFinding && !randomError && (
            <div
              className="mt-6 mx-auto max-w-lg rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/60 shadow-sm px-8 py-10 flex flex-col items-center text-center"
              data-testid="random-empty"
            >
              <div
                aria-hidden="true"
                className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 dark:text-amber-300 mb-4"
              >
                <Dices size={26} strokeWidth={2.25} />
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
                Press &apos;Select a Random Prayer&apos; button to surface a prayer from a random member.
              </h2>
            </div>
          )}

          {!randomHydrating && randomSessionResults.length > 0 && (
            <>
              <div
                className="mb-3 flex items-center gap-2 flex-wrap"
                data-testid="random-session-pills"
              >
                {randomSessionResults.map((p) => {
                  const handle = p.user_handle ? `@${p.user_handle}` : (p.user_name || 'anonymous');
                  const active = randomCurrentId === p.id;
                  return (
                    <div
                      key={p.id}
                      data-testid={`random-session-pill-${p.id}`}
                      className={`inline-flex items-center h-8 rounded-full border text-sm transition-colors ${
                        active
                          ? 'bg-amber-100 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200'
                          : 'bg-zinc-100 dark:bg-zinc-800 border-transparent text-zinc-800 dark:text-zinc-100 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => selectRandomDiscoveryLocal(p.id)}
                        data-testid={`random-session-pill-select-${p.id}`}
                        aria-pressed={active}
                        title={active ? 'Currently showing this prayer' : `Show ${handle}'s prayer`}
                        className="pl-3 pr-2 h-full inline-flex items-center gap-1.5 font-medium"
                      >
                        <Dices size={12} className="opacity-70" />
                        <span className="max-w-[160px] truncate">{handle}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSessionResult(p.id)}
                        data-testid={`random-session-pill-remove-${p.id}`}
                        aria-label={`Remove ${handle} from session results`}
                        className="mr-1 p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 text-current"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {(() => {
                const currentPrayer = randomSessionResults.find((p) => p.id === randomCurrentId)
                  || randomSessionResults[0];
                if (!currentPrayer) return null;
                // Use the exact same responsive grid wrapper as the
                // Shared / Nearby / Search feeds so the single Random
                // card renders at grid-cell width — visually identical
                // to any card on those tabs at every breakpoint. The
                // card naturally lands in the first cell; empty cells
                // stay empty, which is fine because the surrounding
                // pills + CTA carry the "one card at a time" mental
                // model.
                return (
                  <div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-x-4 gap-y-8 pb-10"
                    data-testid="random-session-grid"
                  >
                    <PrayerCard
                      key={currentPrayer.id}
                      prayer={currentPrayer}
                      onDismiss={user ? handleDismiss : undefined}
                    />
                  </div>
                );
              })()}
            </>
          )}

        </div>
      )}

      {!isRandomTab && !isNotifications && gridSection}
    </div>
  );
};

export default Home;
